import { Component, OnInit } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { Router } from "@angular/router";
import { FeatureViews } from "src/app/ui/models/domains/features/custom-feature-views";
import { PageViews } from "src/app/ui/models/domains/features/custom-page-views";
import { MaskInput } from "src/app/ui/models/domains/masks/mask.model";
import { PageableResult } from "src/app/ui/models/domains/paginator/pageable-result";
import { ClusterSearchParameters } from "src/app/ui/models/domains/pbm/clusters/cluster-search-parameters.model";
import { Cluster } from "src/app/ui/models/domains/pbm/clusters/cluster.model";
import { FileExportProcessor } from "src/app/ui/models/domains/pbm/exports/file-export-processor";
import { UserPermissions } from "src/app/ui/models/domains/pbm/user/user-permissions";
import { UserProfile } from "src/app/ui/models/domains/pbm/user/user-profile";
import { RoutePath } from "src/app/ui/models/domains/route-paths.model";
import { FeatureViewService } from "src/app/ui/services/feature-view.service";
import { FileExportService } from "src/app/ui/services/file-export.service";
import { LoadingService } from "src/app/ui/services/loading.service";
import { ClustersService } from "src/app/ui/services/pbm/clusters.service";
import { UserService } from "src/app/ui/services/user.service";
import { Toast } from "../../../toast/toast";
import { SearchClusterExportOrderRequest } from "./search-cluster-export-order-request";

@Component({
  selector: "search-cluster-management",
  templateUrl: "./search-cluster-management.component.html",
  styleUrls: ["./search-cluster-management.component.scss"],
})
export class SearchClusterManagementComponent implements OnInit {
  public filterText: string = "";
  nameControl = new UntypedFormControl();
  public fiscalNumberMask = MaskInput.getFicalNumberMask();
  private searchResult: PageableResult = new PageableResult();
  private clusterSearchParameters: ClusterSearchParameters;
  public fileExportProcessor = new FileExportProcessor();
  public userProfile: UserProfile;

  constructor(
    private featureViewService: FeatureViewService,
    private router: Router,
    private loadingService: LoadingService,
    private toast: Toast,
    private clustersService: ClustersService,
    private fileExportService: FileExportService,
    private userService: UserService,
  ) {
    this.clusterSearchParameters = new ClusterSearchParameters();
  }

  async ngOnInit() {
    this.featureViewService.activeFeatureView(FeatureViews.SearchClusters, PageViews.ClustersManagementPageView);

    this.getClusterSearch();
  }

  public async getClusterSearch(): Promise<void> {
    const loadignRef = this.loadingService.beginLoading();

    try {
      const clusterSearchParameters = new ClusterSearchParameters();
      clusterSearchParameters.setStartIndex(0);
      clusterSearchParameters.setTotalPerPages(10);

      const result = await this.clustersService.getClustersPageableResult(clusterSearchParameters);
      this.searchResult.setStart(0);
      this.searchResult.setEnd(clusterSearchParameters.getTotalPerPages());
      this.searchResult.setTotalItems(result.getTotalItems());
      this.searchResult.addItems(result.getItems());
    } finally {
      loadignRef.closeLoading();
    }
  }

  public hasResult(): boolean {
    return !!this.searchResult;
  }

  public hasClusters(): boolean {
    return !!this.searchResult && this.searchResult.getItems().length > 0;
  }

  public getClusters(): Cluster[] {
    return this.searchResult.getItems() as Cluster[];
  }

  public async exportClusterList(cluster: Cluster): Promise<any> {
    const ref = this.loadingService.beginLoading();

    const clusterFileExportOrderRequest = new SearchClusterExportOrderRequest(this.clustersService, this.toast, this.fileExportService, cluster);
    await this.fileExportProcessor.processFileBySas(clusterFileExportOrderRequest, ref).catch((error) => {
      this.toast.showWarningToast(error);
      ref.closeLoading();
    });
  }

  public async searchClusters(): Promise<void> {
    const loadingRef = this.loadingService.beginLoading();

    try {
      let fiscalNumber: string;

      if (this.filterText) {
        fiscalNumber = this.filterText.replace(/[./-]/g, "");
      } else {
        fiscalNumber = null;
      }

      this.clusterSearchParameters.setStartIndex(0);
      this.clusterSearchParameters.setTotalPerPages(10);
      this.clusterSearchParameters.setFilterText(fiscalNumber);

      this.searchResult = await this.clustersService.getClustersPageableResult(this.clusterSearchParameters);
    } catch (error) {
      this.toast.showWarningToast("Erro ao buscar industria!");
    } finally {
      loadingRef.closeLoading();
    }
  }

  public onPressEnterSearchClusters(event): void {
    if (event.key === "Enter") this.searchClusters();
  }

  public getResultQuantity(): string {
    if (!this.searchResult || this.searchResult.getItems().length === 0) {
      return "Nenhum resultado encontrado.";
    }
    return `${this.searchResult.getItems().length} cluster(s) encontrado(s)`;
  }

  public navigateToEdit(cluster: Cluster): void {
    this.router.navigate([RoutePath.EditCluster, cluster.getClusterKey().getValue()]);
  }

  async searchMoreClusters() {
    const ref = this.loadingService.beginLoading();

    this.clusterSearchParameters.setStartIndex(this.searchResult.getEnd() + 1);
    this.clusterSearchParameters.setTotalPerPages(10);

    try {
      const result = await this.clustersService.getClustersPageableResult(this.clusterSearchParameters);
      this.searchResult.setStart(this.clusterSearchParameters.getStartIndex());
      this.searchResult.setEnd(this.clusterSearchParameters.getTotalPerPages());
      this.searchResult.setTotalItems(result.getTotalItems());
      this.searchResult.addItems(result.getItems());
    } catch (error) {
      this.toast.showWarningToast(error);
    } finally {
      this.loadingService.finishLoading(ref);
    }
  }

  showMoreClustersBtn(): boolean {
    return this.searchResult && this.searchResult.getTotalItems() !== this.searchResult.getItems().length && this.searchResult.getItems().length > 1;
  }

  hasAccessToEditCluster(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.EditCluster);
  }
}
